// Bridge to the embedding parent window's (e.g. Unity) text-to-speech methods,
// exposed over the PenPal connection established in IFrameRemote.svelte.
//
// When the editor runs inside a host that provides local TTS, the host exposes
// two methods on its PenPal remote:
//   - getVoices(): Promise<{ success, message?, voices?, defaultVoice? }>
//   - renderAudio(request): Promise<{ success, message?, url?, ... eventId? }>
//
// IFrameRemote.svelte resolves the PenPal handshake and hands the remote to
// `setParentRemote`. Everything else (VoiceManager, AudioDescriptionEvent,
// RenderAudio) consumes the remote through the helpers below so the handshake
// only needs to happen once.

// Provider id stamped onto voice objects sourced from the parent host. Used to
// branch preview/render logic away from the Firebase cloud path.
export const LOCAL_TTS_PROVIDER = "local";

// Hard limit enforced by the parent renderAudio method (plain text characters).
export const MAX_TTS_CHARS = 5000;

let parentRemote = null;
let resolveRemote;
const remotePromise = new Promise((resolve) => {
    resolveRemote = resolve;
});

/**
 * Store the resolved PenPal remote so the TTS helpers can use it. Called once
 * from IFrameRemote.svelte after `connection.promise` resolves.
 *
 * @param {object} remote - the parent window's PenPal remote/methods object
 */
export function setParentRemote(remote) {
    parentRemote = remote;
    if (resolveRemote) {
        resolveRemote(remote);
    }
}

/**
 * Resolve to the parent PenPal remote, waiting for the handshake if needed.
 *
 * @returns {Promise<object>}
 */
export function getParentRemote() {
    return parentRemote || remotePromise;
}

/**
 * Call the parent host's exportSubtitle method (toolbar CTA).
 * The parent decides what action to take; this only invokes the remote method.
 *
 * @returns {Promise<*>}
 */
export async function callParentExportSubtitle() {
    const remote = await getParentRemote();
    if (!remote || typeof remote.exportSubtitle !== "function") {
        throw new Error("Parent host does not expose exportSubtitle().");
    }
    return remote.exportSubtitle();
}

/**
 * Call the parent host's cancel method (toolbar CTA).
 * The parent decides what action to take; this only invokes the remote method.
 *
 * @returns {Promise<*>}
 */
export async function callParentCancel() {
    const remote = await getParentRemote();
    if (!remote || typeof remote.cancel !== "function") {
        throw new Error("Parent host does not expose cancel().");
    }
    return remote.cancel();
}

/**
 * List the voices the parent host can render.
 *
 * @returns {Promise<{ success: boolean, message?: string, voices?: Array<{ id: string, name: string, language: string | null, gender?: string }>, defaultVoice?: string }>}
 */
export async function getParentVoices() {
    const remote = await getParentRemote();
    if (!remote || typeof remote.getVoices !== "function") {
        throw new Error("Parent host does not expose getVoices().");
    }
    return remote.getVoices();
}

/**
 * Render a single cue through the parent host.
 *
 * @param {object} request
 * @param {string} request.text - plain text, max 5000 chars
 * @param {string} [request.voice] - voice id from getVoices()
 * @param {number} [request.speed] - 0.25-4.0, default 1.0
 * @param {"mp3"|"wav"} [request.format] - mp3 for preview, wav for export
 * @param {string} [request.eventId] - cue id, echoed back in the response
 * @returns {Promise<{ success: boolean, message?: string, url?: string, format?: string, contentType?: string, cached?: boolean, eventId?: string | null }>}
 */
export async function renderParentAudio(request = {}) {
    const remote = await getParentRemote();
    if (!remote || typeof remote.renderAudio !== "function") {
        throw new Error("Parent host does not expose renderAudio().");
    }

    const text = typeof request.text === "string" ? request.text : "";
    if (text.length > MAX_TTS_CHARS) {
        throw new Error(
            `Text exceeds the ${MAX_TTS_CHARS} character limit for local text-to-speech.`,
        );
    }

    return remote.renderAudio({
        text,
        voice: request.voice,
        speed: request.speed,
        format: request.format || "mp3",
        eventId: request.eventId,
    });
}

/**
 * Determine the duration (in seconds) of an audio URL. The parent renderAudio
 * response does not include a duration, so we derive it by loading metadata.
 * Resolves to 0 if the duration cannot be determined.
 *
 * @param {string} url
 * @returns {Promise<number>}
 */
export function getAudioDurationFromUrl(url) {
    return new Promise((resolve) => {
        if (!url) {
            resolve(0);
            return;
        }

        const audio = new Audio();
        let settled = false;

        const cleanup = () => {
            audio.removeEventListener("loadedmetadata", onLoaded);
            audio.removeEventListener("error", onError);
            audio.src = "";
        };

        const finish = (value) => {
            if (settled) return;
            settled = true;
            cleanup();
            resolve(value);
        };

        const onLoaded = () => {
            const duration = audio.duration;
            finish(Number.isFinite(duration) ? duration : 0);
        };

        const onError = () => finish(0);

        audio.addEventListener("loadedmetadata", onLoaded);
        audio.addEventListener("error", onError);
        // Safety timeout so a hung/expired URL never blocks the render loop.
        setTimeout(() => finish(0), 15000);

        audio.preload = "metadata";
        audio.src = url;
    });
}
